import { useMemo } from "react";
import { useGameEvents } from "./useGameEvents";

export type StatsPeriod = 'game' | '1h' | '2h';

interface EventBasedStats {
  goals: { home: number; away: number };
  yellowCards: { home: number; away: number };
  redCards: { home: number; away: number };
  substitutions: { home: number; away: number };
}

export const useEventBasedStats = (
  fixtureId: number | null,
  period: StatsPeriod,
  homeTeamId: string | null,
  awayTeamId: string | null
): { stats: EventBasedStats | null; isLoading: boolean } => {
  const { data: events, isLoading } = useGameEvents(fixtureId);

  const stats = useMemo(() => {
    if (!events || events.length === 0) return null;

    // Filter events by period
    const filteredEvents = events.filter(event => {
      const minute = event.time_elapsed;
      if (period === '1h') {
        return minute <= 45;
      }
      if (period === '2h') {
        return minute > 45 && minute <= 90;
      }
      return true; // 'game' - all events
    });

    // Separate by team
    const homeEvents = filteredEvents.filter(e => e.team_id === homeTeamId);
    const awayEvents = filteredEvents.filter(e => e.team_id === awayTeamId);

    // Count goals (excluding own goals from the team count, adding to opponent)
    const countGoals = (teamEvents: typeof filteredEvents, opponentEvents: typeof filteredEvents) => {
      const regularGoals = teamEvents.filter(
        e => e.event_type === 'Goal' && e.event_detail !== 'Own Goal'
      ).length;
      const ownGoalsAgainst = opponentEvents.filter(
        e => e.event_type === 'Goal' && e.event_detail === 'Own Goal'
      ).length;
      return regularGoals + ownGoalsAgainst;
    };

    // Count cards
    const countYellowCards = (teamEvents: typeof filteredEvents) =>
      teamEvents.filter(
        e => e.event_type === 'Card' && e.event_detail?.includes('Yellow')
      ).length;

    const countRedCards = (teamEvents: typeof filteredEvents) =>
      teamEvents.filter(
        e => e.event_type === 'Card' && (e.event_detail === 'Red Card' || e.event_detail === 'Second Yellow card')
      ).length;

    // Count substitutions
    const countSubs = (teamEvents: typeof filteredEvents) =>
      teamEvents.filter(e => e.event_type === 'subst').length;

    return {
      goals: {
        home: countGoals(homeEvents, awayEvents),
        away: countGoals(awayEvents, homeEvents),
      },
      yellowCards: {
        home: countYellowCards(homeEvents),
        away: countYellowCards(awayEvents),
      },
      redCards: {
        home: countRedCards(homeEvents),
        away: countRedCards(awayEvents),
      },
      substitutions: {
        home: countSubs(homeEvents),
        away: countSubs(awayEvents),
      },
    };
  }, [events, period, homeTeamId, awayTeamId]);

  return { stats, isLoading };
};
